import {
  Badge,
  Button,
  DataTable,
  Input,
  type TanstackReactTable,
  useDebounceCallback,
} from "@devhop/ui";
import { Check, ChevronDown, ChevronRight, Undo2 } from "lucide-react";
import { useMemo, useState } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import {
  isValidCfCode,
  normalizeCfCode,
} from "@/modules/cf-feed/domain/lib/cf-code";
import {
  useDefaultCodesQuery,
  useReverseVariantCfStock,
  useSetDefaultCode,
} from "@/modules/cf-feed/presentation/api/queries";
import type {
  ProductVariantDefaultCodeItem,
  ProductVariantDefaultCodeProduct,
} from "@/modules/cf-feed/presentation/api/types";
import {
  formatKip,
  Thumb,
} from "@/modules/cf-feed/presentation/ui/table-helpers";
import { QueryState } from "@/shared/ui/QueryState";

export function ProductDefaultCodesPage() {
  const [searchInput, setSearchInput] = useState("");
  const [q, setQ] = useState("");
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [offset, setOffset] = useState(0);
  const [limit, setLimit] = useState(20);
  const [expandedProductIds, setExpandedProductIds] = useState<Set<string>>(
    () => new Set(),
  );

  const debouncedSearch = useDebounceCallback((value: string) => {
    setQ(value.trim());
    setOffset(0);
    setExpandedProductIds(new Set());
  }, 350);

  const defaultsQuery = useDefaultCodesQuery(q || undefined, {
    groupByProduct: true,
    limit,
    offset,
  });
  const setDefaultCode = useSetDefaultCode();
  const reverseStock = useReverseVariantCfStock();

  const columns = useMemo<
    TanstackReactTable.ColumnDef<ProductVariantDefaultCodeItem>[]
  >(
    () => [
      {
        id: "product",
        header: "ສິນຄ້າ",
        cell: ({ row }) => (
          <div className="flex min-w-[220px] items-center gap-2">
            <Thumb src={row.original.imageUrl} alt={row.original.name} />
            <div className="min-w-0">
              <p className="line-clamp-2 font-medium">{row.original.name}</p>
              <p className="truncate text-muted-foreground text-xs">
                SKU: {row.original.sku}
              </p>
            </div>
          </div>
        ),
      },
      {
        id: "cfCode",
        header: "ລະຫັດ CF ເລີ່ມຕົ້ນ",
        cell: ({ row }) => {
          const item = row.original;
          const value = draft[item.variantId] ?? item.cfCode ?? "";
          const normalized = normalizeCfCode(value);
          const dirty =
            draft[item.variantId] != null && normalized !== (item.cfCode ?? "");
          const invalid = value.trim() !== "" && !isValidCfCode(normalized);
          const canSave = dirty && isValidCfCode(normalized);
          return (
            <div>
              <div className="flex items-center gap-1">
                <Input
                  className="h-8 w-28 font-mono text-xs uppercase"
                  maxLength={10}
                  value={value}
                  onChange={(e) =>
                    setDraft((prev) => ({
                      ...prev,
                      [item.variantId]: e.target.value,
                    }))
                  }
                  onBlur={() => {
                    if (value.trim()) {
                      setDraft((prev) => ({
                        ...prev,
                        [item.variantId]: normalized,
                      }));
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && canSave) {
                      e.preventDefault();
                      setDefaultCode.mutate({
                        variantId: item.variantId,
                        cfCode: normalized,
                      });
                    }
                  }}
                />
                <Button
                  type="button"
                  size="icon"
                  variant={dirty ? "default" : "ghost"}
                  className="size-8"
                  disabled={!canSave || setDefaultCode.isPending}
                  onClick={() => {
                    setDefaultCode.mutate({
                      variantId: item.variantId,
                      cfCode: normalized,
                    });
                  }}
                  title="ບັນທຶກລະຫັດ CF"
                >
                  <Check className="size-4" />
                </Button>
              </div>
              {invalid ? (
                <p className="mt-1 text-destructive text-xs">ບໍ່ຖືກຕ້ອງ</p>
              ) : null}
            </div>
          );
        },
        size: 180,
      },
      {
        id: "price",
        header: "ລາຄາ POS",
        cell: ({ row }) => formatKip(row.original.price),
        size: 110,
      },
      {
        id: "stock",
        header: "ພ້ອມຂາຍ",
        cell: ({ row }) => (
          <div>
            <p className="font-medium">{row.original.stockOnHand}</p>
            <p className="text-muted-foreground text-xs">
              ເພີ່ມໄດ້ {row.original.availableToAdd}
            </p>
          </div>
        ),
        size: 110,
      },
      {
        id: "allocated",
        header: "ໃນລະບົບ CF",
        cell: ({ row }) => (
          <span className="font-medium">
            {row.original.allocatedByOtherPosts}
          </span>
        ),
        size: 120,
      },
      {
        id: "reverse",
        header: "ຈັດການ",
        cell: ({ row }) => {
          const inCf = row.original.allocatedByOtherPosts;
          return (
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={inCf <= 0 || reverseStock.isPending}
              onClick={() => {
                if (
                  !window.confirm(
                    `ຄືນຈຳນວນທີ່ຈອງໃນ CF (${inCf} ຊິ້ນ) ຂອງ "${row.original.name}" ເຂົ້າສະຕັອກຫຼັກ? ຈະອັບເດດ session ແລະ ຮ່າງ Live ທີ່ກ່ຽວຂ້ອງ`,
                  )
                ) {
                  return;
                }
                void reverseStock.mutateAsync(row.original.variantId);
              }}
              title="ຄືນເຂົ້າສະຕັອກຫຼັກ"
            >
              <span className="inline-flex items-center gap-1.5">
                <Undo2 className="size-4" aria-hidden />
                ຄືນຄັງ
              </span>
            </Button>
          );
        },
        size: 140,
      },
    ],
    [draft, setDefaultCode, reverseStock],
  );

  const groupedColumns = useMemo<
    TanstackReactTable.ColumnDef<ProductVariantDefaultCodeProduct>[]
  >(
    () => [
      {
        id: "product",
        header: "ສິນຄ້າ",
        cell: ({ row }) => {
          const product = row.original;
          const expanded = expandedProductIds.has(product.productId);
          const configuredCount = product.variants.filter(
            (item) => item.cfCode,
          ).length;
          return (
            <div className="min-w-[520px]">
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  className="size-8 shrink-0"
                  aria-label={
                    expanded
                      ? `ຫຸບຕົວເລືອກຂອງ ${product.name}`
                      : `ເບິ່ງຕົວເລືອກຂອງ ${product.name}`
                  }
                  aria-expanded={expanded}
                  onClick={() =>
                    setExpandedProductIds((prev) => {
                      const next = new Set(prev);
                      if (next.has(product.productId))
                        next.delete(product.productId);
                      else next.add(product.productId);
                      return next;
                    })
                  }
                >
                  {expanded ? (
                    <ChevronDown className="size-4" />
                  ) : (
                    <ChevronRight className="size-4" />
                  )}
                </Button>
                <Thumb src={product.variants[0]?.imageUrl} alt={product.name} />
                <div className="min-w-0 flex-1">
                  <p className="font-medium">{product.name}</p>
                  <div className="mt-1 flex flex-wrap items-center gap-2">
                    <Badge variant="secondary" className="font-normal">
                      {product.variants.length} ຕົວເລືອກ
                    </Badge>
                    <span className="text-muted-foreground text-xs">
                      ຕັ້ງລະຫັດແລ້ວ {configuredCount}/{product.variants.length}
                    </span>
                  </div>
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="shrink-0"
                  onClick={() =>
                    setExpandedProductIds((prev) => {
                      const next = new Set(prev);
                      if (next.has(product.productId))
                        next.delete(product.productId);
                      else next.add(product.productId);
                      return next;
                    })
                  }
                >
                  {expanded ? "ຫຸບ" : "ເບິ່ງ"}
                </Button>
              </div>
              {expanded ? (
                <div className="mt-3 space-y-2 border-muted border-t pt-2">
                  {product.variants.map((item) => (
                    <DefaultCodeVariantRow
                      key={item.variantId}
                      item={item}
                      draft={draft[item.variantId]}
                      isSaving={setDefaultCode.isPending}
                      isReversing={reverseStock.isPending}
                      onDraftChange={(value) =>
                        setDraft((prev) => ({
                          ...prev,
                          [item.variantId]: value,
                        }))
                      }
                      onSave={(cfCode) =>
                        setDefaultCode.mutate({
                          variantId: item.variantId,
                          cfCode,
                        })
                      }
                      onReverse={() => {
                        void reverseStock.mutateAsync(item.variantId);
                      }}
                    />
                  ))}
                </div>
              ) : null}
            </div>
          );
        },
      },
    ],
    [draft, expandedProductIds, reverseStock, setDefaultCode],
  );

  return (
    <>
      <Header />
      <Main>
        <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="font-semibold text-xl">ລະຫັດ CF ເລີ່ມຕົ້ນ</h1>
            <p className="text-muted-foreground text-sm">
              ກຳນົດລະຫັດຕໍ່ຕົວເລືອກ ເພື່ອໃຊ້ຕອນເພີ່ມສິນຄ້າເຂົ້າໂພສ — ຖ້າຍັງບໍ່ມີ ລະບົບສ້າງຈາກ
              SKU/barcode ອັດຕະໂນມັດ (ບໍ່ຊ້ຳ)
            </p>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row">
            <Input
              className="w-full sm:w-[260px]"
              placeholder="ຄົ້ນຫາຊື່ / SKU / barcode"
              value={searchInput}
              onChange={(e) => {
                setSearchInput(e.target.value);
                debouncedSearch(e.target.value);
              }}
            />
          </div>
        </div>

        <QueryState result={defaultsQuery}>
          {defaultsQuery.data?.products ? (
            <DataTable<ProductVariantDefaultCodeProduct, unknown>
              data={defaultsQuery.data.products}
              columns={groupedColumns}
              isLoading={defaultsQuery.isFetching}
              noDataMessage="ບໍ່ພົບສິນຄ້າ"
              offset={offset}
              limit={limit}
              totalCount={defaultsQuery.data.total}
              onPaginationChange={(page) => {
                setOffset(page.offset);
                setLimit(page.limit);
              }}
            />
          ) : (
            <DataTable<ProductVariantDefaultCodeItem, unknown>
              data={defaultsQuery.data?.items ?? []}
              columns={columns}
              isLoading={defaultsQuery.isFetching}
              noDataMessage="ບໍ່ພົບສິນຄ້າ"
              offset={0}
              limit={Math.max(defaultsQuery.data?.items.length ?? 0, 1)}
              totalCount={defaultsQuery.data?.total ?? 0}
              onPaginationChange={() => {}}
            />
          )}
        </QueryState>
      </Main>
    </>
  );
}

function DefaultCodeVariantRow({
  item,
  draft,
  isSaving,
  isReversing,
  onDraftChange,
  onSave,
  onReverse,
}: {
  item: ProductVariantDefaultCodeItem;
  draft?: string;
  isSaving: boolean;
  isReversing: boolean;
  onDraftChange: (value: string) => void;
  onSave: (cfCode: string) => void;
  onReverse: () => void;
}) {
  const value = draft ?? item.cfCode ?? "";
  const normalized = normalizeCfCode(value);
  const dirty = draft != null && normalized !== (item.cfCode ?? "");
  const invalid = value.trim() !== "" && !isValidCfCode(normalized);
  const canSave = dirty && isValidCfCode(normalized);
  const inCf = item.allocatedByOtherPosts;

  return (
    <div className="grid gap-2 rounded-lg border bg-muted/20 p-2 text-xs md:grid-cols-[minmax(150px,1fr)_190px_100px_110px_130px] md:items-center">
      <div className="flex min-w-0 items-center gap-2">
        <Thumb src={item.imageUrl} alt={item.name} />
        <div className="min-w-0">
          <p className="font-medium font-mono">{item.sku}</p>
          <p className="truncate text-muted-foreground">
            {item.name} · {formatKip(item.price)}
          </p>
        </div>
      </div>
      <div>
        <div className="flex items-center gap-1">
          <Input
            className="h-8 w-28 font-mono text-xs uppercase"
            maxLength={10}
            value={value}
            onChange={(event) => onDraftChange(event.target.value)}
            onBlur={() => {
              if (value.trim()) onDraftChange(normalized);
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter" && canSave) {
                event.preventDefault();
                onSave(normalized);
              }
            }}
          />
          <Button
            type="button"
            size="icon"
            variant={dirty ? "default" : "ghost"}
            className="size-8"
            disabled={!canSave || isSaving}
            onClick={() => onSave(normalized)}
            title="ບັນທຶກລະຫັດ CF"
          >
            <Check className="size-4" />
          </Button>
        </div>
        {invalid ? <p className="mt-1 text-destructive">ບໍ່ຖືກຕ້ອງ</p> : null}
      </div>
      <div>
        <p className="font-medium">{item.stockOnHand}</p>
        <p className="text-muted-foreground">ເພີ່ມໄດ້ {item.availableToAdd}</p>
      </div>
      <div>
        <p className="font-medium">{inCf}</p>
        <p className="text-muted-foreground">ໃນລະບົບ CF</p>
      </div>
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={inCf <= 0 || isReversing}
        onClick={() => {
          if (
            window.confirm(
              `ຄືນຈຳນວນທີ່ຈອງໃນ CF (${inCf} ຊິ້ນ) ຂອງ "${item.name}" ເຂົ້າສະຕັອກຫຼັກ? ຈະອັບເດດ session ແລະ ຮ່າງ Live ທີ່ກ່ຽວຂ້ອງ`,
            )
          ) {
            onReverse();
          }
        }}
        title="ຄືນເຂົ້າສະຕັອກຫຼັກ"
      >
        <span className="inline-flex items-center gap-1.5">
          <Undo2 className="size-4" aria-hidden />
          ຄືນຄັງ
        </span>
      </Button>
    </div>
  );
}
